/**
 * DASHBOARD-RPS-TEAMS.WIDGET
 * Repartition of users for a survey
 */
import Card from "@/components/card"
import { useEffect, useState } from "react"
import { DashboardFilters, Filter, FilterLite } from "@/redux/filter.types"
import { store } from "@/index"
import { INSUFFICIENT_PARTICIPATION, STATUS_LOADED, STATUS_LOADING } from "@/redux/_status.types"
import { fetchGollacTeams } from "@/redux/_rps.actions"
import Dropdown from "@/components/dropdown"
import Space from "@/components/space"
import NoResultsWidget from "./no-results.widget"
import initFilters from "@/utils/init-filters.utils"
import { t } from "i18next"
import { useAppSelector } from "@/core/root-reducer"
import Plot from "react-plotly.js"
import { PLOTLY_HOVERLABEL, PLOTLY_HOVERTEMPLATE_STACKED_BARS } from "@/utils/plotly-hover.utils"
import PagePlaceholder from "@/components/page-placeholder"
import Tooltip from "@/components/tooltip"
import Link from "@/components/link"
import { filterReplaceDashboard } from "@/redux/filter.actions"
import DashboardRpsTeamsSkeletonWidget from "./skeletons/dashboard-rps-teams-skeleton.widget"
import PlotReveal from "@/components/plot-reveal"
import { RPS_BAR_TEXT } from "@/utils/rps-bar-text.utils"
import { fetchRepartition } from "@/redux/_archive.actions"
import { getRepartitionGroups } from "@/utils/get-repartition-ceils.utils"

interface Props {
  filterName?: string
  isReadOnly?: boolean
  isReport?: boolean
}

//Height of one team row, as in the Karasek chart : the names, the chips and the bands of the chart must match
const ROW_HEIGHT = 37

//Chip of the share of exposed employees : solid red, with the detail on hover
//Nobody exposed : no chip (a 0 % would only raise questions). A share that rounds to 0 while somebody is exposed stays visible
function ExposedChip({ exposed, total }: { exposed: number; total: number }) {
  if (total <= 0 || exposed <= 0) return null
  const share = Math.round((exposed / total) * 100)
  return (
    <Tooltip text={t("rps_teams_exposed_detail", { exposed, total })} position="top" usePortal>
      <span
        className="text-sm font-bold"
        style={{
          backgroundColor: "#eb5a46",
          borderRadius: 999,
          color: "#fff",
          display: "inline-block",
          boxSizing: "border-box",
          minWidth: 52,
          padding: "1px 8px",
          textAlign: "center",
          whiteSpace: "nowrap"
        }}>
        {share === 0 ? "< 1 %" : `${share} %`}
      </span>
    </Tooltip>
  )
}

export function DashboardRpsGollacTeamsWidget(props: Props) {
  // selectors
  const _session = useAppSelector((state) => state._session)
  const filter = useAppSelector((state) => state.filter)

  // states
  const [currentFilter, setCurrentFilter] = useState<Filter>(initCurrentFilter())
  const [status, setStatus] = useState(STATUS_LOADING)
  const [teams, setTeams] = useState<any>([])
  const [overall, setOverall] = useState<{ exposed: number; total: number } | null>(null) //All the people of the survey (with the filters of the dashboard), to compare each population with
  const [noData, setNoData] = useState(false)
  const [data, setData] = useState<Plotly.Data[]>([])

  //At least one chip is shown : somebody is exposed in a population or in the whole account
  const hasExposed = teams.some((team: any) => Number(team.exposed) > 0) || (overall?.exposed ?? 0) > 0

  //Name of the account when the dashboard is not filtered, otherwise the figure is not the one of the whole account
  const isFiltered = Boolean(
    filter.dashboard.gender ||
      filter.dashboard.birthDate ||
      filter.dashboard.companyWelcomeDate ||
      filter.dashboard.customFilters.length ||
      filter.dashboard.accounts.length
  )
  const overallLabel = !isFiltered && _session.accountName ? _session.accountName : t("rps_teams_overall")

  useEffect(() => {
    setCurrentFilter(initCurrentFilter())
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filterName])

  useEffect(() => {
    let searchFilter

    if (props.filterName) {
      searchFilter = filter.list.find((x) => x.name === props.filterName)
    } else if (_session.accountOptions.heatmapFilterName) {
      searchFilter = filter.list.find((x) => x.name === _session.accountOptions.heatmapFilterName)
    }

    setCurrentFilter(searchFilter || (filter.list.length ? filter.list[0] : new Filter()))
  }, [props.filterName, filter.list, _session.accountOptions])

  useEffect(() => {
    if (!currentFilter) return

    async function load() {
      setStatus(STATUS_LOADING)

      const response: any = await store.dispatch(
        fetchGollacTeams(currentFilter.name, filter.dashboard, undefined, undefined, props.isReport)
      )

      setStatus(STATUS_LOADED)

      //Same figures as the overview : the notes of every person, the exposed ones being in the first group of the scale
      const repartition: any = await store.dispatch(
        fetchRepartition(filter.dashboard, false, null, null, undefined, undefined, props.isReport, true)
      )
      const values: number[] = !repartition.error && Array.isArray(repartition.values) ? repartition.values.map(Number) : []
      const total = values.reduce((sum, value) => sum + value, 0)
      const exposedGroup = getRepartitionGroups(_session, true, false)[0]
      setOverall(
        total > 0
          ? { exposed: values.slice(exposedGroup.min - 1, exposedGroup.max).reduce((sum, value) => sum + value, 0), total }
          : null
      )

      const isEmpty =
        response.message === "no_data" ||
        (Array.isArray(response.teams_records) && response.teams_records.length === 0) ||
        (Array.isArray(response.teams) && response.teams.length === 0)

      if (isEmpty) {
        setNoData(true)
        setTeams([])
        setData([])
        return
      }

      if (!response.error) {
        const sorted = [...response.teams].sort((a, b) => b.exposed - a.exposed || b.exposed_pct - a.exposed_pct)

        setTeams(sorted)
        setData(buildPlotData(sorted))
      }
    }

    load()
  }, [currentFilter, filter.dashboard])

  function buildPlotData(sortedTeams: any[]): Plotly.Data[] {
    return [
      {
        x: sortedTeams.map((t) => t.exposed),
        y: sortedTeams.map((t) => t.attribute_name),
        text: sortedTeams.map((t) => t.exposed),
        ...RPS_BAR_TEXT,
        name: t("employees_exposed"),
        hovertemplate: PLOTLY_HOVERTEMPLATE_STACKED_BARS,
        orientation: "h",
        type: "bar",
        marker: { color: "#eb5a46" }
      },
      {
        x: sortedTeams.map((t) => t.safe),
        y: sortedTeams.map((t) => t.attribute_name),
        text: sortedTeams.map((t) => t.safe),
        ...RPS_BAR_TEXT,
        name: t("employees_safe"),
        hovertemplate: PLOTLY_HOVERTEMPLATE_STACKED_BARS,
        orientation: "h",
        type: "bar",
        marker: { color: "#20CA7E" }
      }
    ]
  }

  const chartHeight = Math.max(teams.length, 1) * ROW_HEIGHT
  const layout: any = {
    hoverlabel: PLOTLY_HOVERLABEL,
    margin: {
      l: 0,
      r: 0,
      b: 0,
      t: 0
    },
    showlegend: false,
    bargap: 0.1,
    barcornerradius: 10,
    barmode: "stack",
    xaxis: {
      showgrid: false,
      showline: false,
      zeroline: false,
    },
    yaxis: {
      showgrid: false,
      showline: false,
      autorange: "reversed",
      zeroline: false,
    },
    height: chartHeight
  }

  //Return default heatmap filter
  function initCurrentFilter() {
    let searchFilter: Filter | undefined = undefined
    if (props.filterName) {
      searchFilter = filter.list.find((x: Filter) => x.name === props.filterName)
    } else if (_session.accountOptions.heatmapFilterName) {
      searchFilter = filter.list.find((x: Filter) => x.name === _session.accountOptions.heatmapFilterName)
    }

    if (searchFilter) {
      return searchFilter
    } else {
      return filter.list.length ? filter.list[0] : new Filter()
    }
  }

  function clickTeams(team) {
    store.dispatch(
      filterReplaceDashboard(
        new DashboardFilters({
          customFilters: [new FilterLite({ id: currentFilter.id, attributesIds: String(team.attribute_id).split("_") })]
        })
      )
    )
  }

  return status === INSUFFICIENT_PARTICIPATION ? (
    <NoResultsWidget />
  ) : (
    <Card className="flex1" status={status} helpSection="onglets" skeleton={<DashboardRpsTeamsSkeletonWidget />}>
      {noData ? (
        <PagePlaceholder
          image="sherlock"
          title={t("rps_teams_no_data_title")}
          help={t("survey_no_results_help", { count: _session.participationMin })}
        />
      ) : (
        <>
          {!props.isReport && (
            <div className="flex" style={{ paddingTop: 12 }}>
              <div>
                <h2>{t("rps_teams_title")}</h2>
                <p className="grey-t text-sm">{t("rps_teams_participation_min", { min: _session.participationMin })}</p>
              </div>
              <Space />
              {props.isReadOnly ? (
                <p>{currentFilter.name}</p>
              ) : (
                <Dropdown
                  displayField="name"
                  list={initFilters(true, true)}
                  onSelect={(filter: Filter) => setCurrentFilter(filter)}
                  value={currentFilter.name}
                />
              )}
            </div>
          )}
        </>
      )}

      {/* Same layout as the Karasek chart (names, chips, chart) : the two charts look the same */}
      {overall && teams.length > 0 && (
        <div
          className="flex"
          style={{ alignItems: "center", borderBottom: "1px solid #e3e3e8", height: ROW_HEIGHT, marginTop: 12, width: "100%" }}>
          <div className="font-bold" style={{ flex: "0 0 200px", minWidth: 0 }}>
            {overallLabel}
          </div>
          <div style={{ flex: "0 0 124px", minWidth: 0 }}>
            <div style={{ alignItems: "center", display: "flex", height: ROW_HEIGHT, justifyContent: "flex-end", paddingRight: 14 }}>
              <ExposedChip exposed={overall.exposed} total={overall.total} />
            </div>
          </div>
        </div>
      )}

      <div
        className="flex"
        style={{ marginTop: overall ? "6px" : "12px", width: "100%", minWidth: 0, overflow: "hidden", alignItems: "flex-start" }}>
        <div style={{ flex: "0 0 200px", minWidth: 0 }}>
          {teams.map((team: any) => (
            <div
              key={team.attribute_id}
              title={team.attribute_name}
              style={{ alignItems: "center", display: "flex", height: ROW_HEIGHT, marginRight: 20, minWidth: 0 }}>
              <Link className="rps-team-name" isWithoutMargin onClick={() => clickTeams(team)}>
                {team.attribute_name}
              </Link>
            </div>
          ))}
        </div>

        <div style={{ flex: "0 0 124px", minWidth: 0 }}>
          {teams.map((team: any) => (
            <div
              key={team.attribute_id}
              style={{ alignItems: "center", display: "flex", height: ROW_HEIGHT, justifyContent: "flex-end", paddingRight: 14 }}>
              <ExposedChip exposed={Number(team.exposed)} total={Number(team.exposed) + Number(team.safe)} />
            </div>
          ))}
        </div>

        <div style={{ flex: "1 1 auto", minWidth: 0, overflow: "hidden" }}>
          <PlotReveal>
            <Plot
              data={data}
              layout={layout}
              config={{
                displayModeBar: false
              }}
              useResizeHandler
              style={{ width: "100%", height: chartHeight }}
            />
          </PlotReveal>
        </div>
      </div>

      {/* The legend only appears when a chip is shown */}
      {teams.length > 0 && hasExposed && (
        <div className="grey-t text-sm flex" style={{ marginTop: 12, marginLeft: 200, gap: 20 }}>
          <span className="inline-flex items-center" style={{ gap: 6 }}>
            <span style={{ backgroundColor: "#eb5a46", borderRadius: 999, height: 8, width: 8 }} />
            {t("rps_teams_exposed_legend")}
          </span>
        </div>
      )}
    </Card>
  )
}
